/**
 * The output frame: its default, its presets, and the crop arithmetic a shape change needs —
 * STUDIO_DESIGN.md 6.2 (`model/frame.ts`) and 6.3.
 *
 * The default is the FIRST clip's own size (owner ruling, §6.3: "Frame default from the first
 * clip's probe … even-rounded, clamped; `touched` latch; shape change re-centres crops with
 * one announcement"), oriented by that clip's user rotate, clamped into the schema's
 * `16..4096` and rounded to an even number of pixels -- odd is a structural refusal
 * (`composition-v1.schema.json:74-85`, `multipleOf: 2`), not a rendering nicety.
 *
 * `touched` is a latch, not a flag: once the user has chosen a frame, adding a second photo
 * must not move it back to the first photo's shape under them.
 *
 * WHO OWNS THE FRAME (whole-branch review, I6). `doc.frame` is the PERSISTED TRUTH -- what
 * history snapshots, what `toWire` sends, what a reload reads back. `FrameState` is the
 * editing view of it, and the ONE bit it adds that the wire has no field for is the `touched`
 * latch, which lives beside the document in the persisted `StudioUiState` (`frameTouched`).
 * A view therefore never constructs `initialFrameState()` on a render: it calls
 * `frameStateFor(doc, ui)`, which is derived, and it writes back with `toFrame(state)` plus
 * `frameTouched: state.touched`. Two consequences, both the point:
 *   - an inline `initialFrameState()` per render can never latch, so the owner ruling above
 *     would be unimplementable;
 *   - an undo restores BOTH halves, because both are inside the snapshot the history stack
 *     holds (`{doc, ui}`) -- where a separately-held `FrameState` would survive the undo and
 *     the next `toFrame(state)` would quietly re-apply the frame the user just undid.
 *
 * Changing the frame's SHAPE invalidates every crop in the project, because
 * `CompositionValidator` refuses a crop whose aspect is more than 1% from the frame's,
 * measured in the ORIENTED space (`CompositionValidator.kt:193-215`). `recentreCrop` is what
 * 6.3's "shape change re-centres crops with one announcement" means arithmetically: keep the
 * user's centre, take the new shape, stay inside the source.
 *
 * PURE. No DOM, no React, no services: the frame is a number pair and a latch, and the panel
 * that edits it is P5b's.
 */
import { MIN_CROP_FRACTION, round6 } from './composition';
import type { Crop, Fit, Frame, StudioDocument } from './composition';

/** The schema's bounds on `frame.width` / `frame.height`. */
export const FRAME_MIN = 16;
export const FRAME_MAX = 4096;

/**
 * The frame a project with no clips opens on, and the fallback for a source whose size the
 * browser could not read. 16:9 at 720p: the shape most photos and every screen recording
 * land near, and small enough that a mistaken default costs nothing to re-render.
 */
const FALLBACK_SIZE: Size = { width: 1280, height: 720 };

/** The schema's `fps` enum (`composition-v1.schema.json:86-94`). Anything else is a refusal. */
export const FPS_CHOICES = [24, 25, 30, 50, 60] as const;
export type Fps = typeof FPS_CHOICES[number];

/**
 * 30 fps, which is what `frame-grid.ts` is quietly easiest at: the 500 ms default boundary is
 * exactly 15 frames there, so the grid never moves a number the user typed.
 */
const DEFAULT_FPS: Fps = 30;

/**
 * Blurred sides, since round 4 (owner: "Bake the Blur sides the default Fit in frame option",
 * A5). Fill (COVER) stays one click away in the frame control and per clip. Under this default
 * every photo is shown WHOLE, so a second photo of another shape never starts life cropped.
 */
export const DEFAULT_FIT: Fit = 'CONTAIN_BLUR';

export interface Size {
    width: number;
    height: number;
}

export interface FramePreset {
    id: string;
    label: string;
    /** `width / height`, or null for "keep the source's own shape". */
    ratio: number | null;
}

export const FRAME_PRESETS: readonly FramePreset[] = Object.freeze([
    { id: 'original', label: 'Original', ratio: null },
    { id: 'landscape', label: '16:9', ratio: 16 / 9 },
    { id: 'portrait', label: '9:16', ratio: 9 / 16 },
    { id: 'square', label: '1:1', ratio: 1 },
    { id: 'social', label: '4:5', ratio: 4 / 5 },
    { id: 'classic', label: '4:3', ratio: 4 / 3 },
]);

/**
 * Even, and inside the schema's range. Both halves are refusals, not preferences.
 *
 * `Math.round(n / 2) * 2` rounds to the NEAREST even number and, on the exact tie every odd
 * integer produces, UP: 1081 becomes 1082. That direction is deliberate -- growing a frame by
 * one pixel letterboxes nothing, while shrinking it silently drops a column of the user's
 * image. (The plan's spec block expected 1080; the pre-flight's R5 rules the expectation
 * wrong, not the code.)
 */
export function evenClamp(n: number): number {
    const even = Math.round(n / 2) * 2;
    return Math.min(FRAME_MAX, Math.max(FRAME_MIN, even));
}

/**
 * The frame a project opens on: the first clip's own pixels, in the ORIENTED space.
 *
 * Scaled down when a side exceeds 4096 and up when one is under 16, aspect preserved in both
 * directions, then evened. A 1x1 favicon and an 8000x6000 DSLR frame both have to produce a
 * legal frame, because both can be the first file a user drops. A source whose aspect is so
 * extreme that no legal frame preserves it (a 1x9000 strip) is clamped rather than refused:
 * an editor that will not open is worse than a frame the user can change.
 *
 * A VIDEO first clip takes the same path from its probe's `width`/`height`; there is no
 * second rule for video, and an animated image is a VIDEO clip (§6.3).
 */
export function frameFromFirstClip(natural: Size, rotate: 0 | 90 | 180 | 270 = 0): Size {
    const swapped = rotate === 90 || rotate === 270;
    let w = swapped ? natural.height : natural.width;
    let h = swapped ? natural.width : natural.height;
    if (!(w > 0) || !(h > 0)) return { ...FALLBACK_SIZE };
    const down = Math.min(1, FRAME_MAX / Math.max(w, h));
    w *= down;
    h *= down;
    const up = Math.max(1, FRAME_MIN / Math.min(w, h));
    w *= up;
    h *= up;
    return { width: evenClamp(w), height: evenClamp(h) };
}

/** A size both of whose sides are finite and positive, else null. */
function positiveSize(size: Size | null | undefined): Size | null {
    if (!size) return null;
    const { width, height } = size;
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? { width, height } : null;
}

/** `b` is `a` with its sides swapped -- what the edge answers for an EXIF 5–8 still (R14-S12). */
export function isTransposeOf(a: Size, b: Size): boolean {
    return a.width === b.height && a.height === b.width && a.width !== a.height;
}

/**
 * A still's DISPLAY size from the two witnesses that can report one (R14-S12, owner O1).
 *
 * `local` is this browser's own decode of the bytes at the pick -- `createImageBitmap(…,
 * {imageOrientation:'from-image'})` or an `<img>`'s `naturalWidth`/`naturalHeight` -- so it is
 * ALREADY in the oriented space (`media/decode.ts` `DecodedOk.natural`). `edge` is the answer of
 * `/files/input/info` as `edgeNaturalOf` reads it. Before ironman `d6e4c3d1` (R14-SRV6) that answer
 * was `vipsheader`'s `width`/`height` alone -- the STORED size, no orientation (`ImageInfo.effectiveWidth`
 * was `width` unswapped, unlike `VideoInfo`'s); measured on the dev edge for a 6048 × 4536
 * Orientation-6 JPEG: local 4536 × 6048, edge `{"width":6048,"height":4536}` -- a frame defaulted
 * from it is the photo's TRANSPOSE, and the stage blurs its sides for ever. Since `d6e4c3d1` the edge
 * also answers `display_width`/`display_height` (the stored pair swapped for orientation 5–8) and
 * `orientation`, and the two witnesses agree; an older edge still answers the stored size alone.
 *
 * So the LOCAL decode is authoritative whenever there is one: it saw the very bytes, oriented, and
 * ironman composes on the EXIF-normalised source (`FilterGraphBuilder.kt:163`), so the oriented
 * size is also what the render fills. The edge's answer stands in only when nothing here decoded
 * the file -- a restored project (no bytes in this tab), an HEIC on Chrome, a decode that ran out
 * of time. Both absent, or non-positive: null, and the frame keeps its fallback until one lands.
 */
export function reconcileNatural(local: Size | null | undefined, edge: Size | null | undefined): Size | null {
    return positiveSize(local) ?? positiveSize(edge);
}

/** The subset of `/files/input/info`'s image answer this rule reads (`InputFileInfo` carries more). */
export interface EdgeStillInfo {
    width?: number | string | null;
    height?: number | string | null;
    display_width?: number | string | null;
    display_height?: number | string | null;
}

/**
 * The edge's natural size for a still, as the frame should read it (R14-S12 for R14-SRV6): the
 * DISPLAY pair (`display_width` × `display_height`, ironman `d6e4c3d1`: the stored grid swapped for
 * EXIF orientation 5–8, equal to it otherwise) when both are present and positive, else the stored
 * `width` × `height` an older edge answers. `orientation` is informational -- the display pair already
 * has it applied -- and is deliberately not read here: nothing may swap twice. Null when neither pair
 * is usable.
 */
export function edgeNaturalOf(info: EdgeStillInfo | null | undefined): Size | null {
    if (!info) return null;
    const display = positiveSize({ width: Number(info.display_width), height: Number(info.display_height) });
    if (display) return display;
    return positiveSize({ width: Number(info.width), height: Number(info.height) });
}

/**
 * The same project at a new shape, keeping roughly the pixel count so a 16:9 → 9:16 switch
 * does not silently quadruple the render cost (`studio.maxOutputMegapixelFrames` is a
 * refusal, and a preset button that trips it would be the editor's own fault).
 */
export function frameForRatio(base: Size, ratio: number): Size {
    const pixels = Math.max(1, base.width * base.height);
    let height = Math.sqrt(pixels / ratio);
    let width = height * ratio;
    // Round 6 (I3): an 11-megapixel first photo at 9:16 is 2508 x 4458 -- and clamping the long side
    // alone gave 2508 x 4096, which is not 9:16, so the Shape select could not even name it. Scale
    // BOTH sides so the schema's ceiling never changes the shape the user asked for.
    const down = Math.min(1, FRAME_MAX / Math.max(width, height));
    width *= down;
    height *= down;
    return { width: evenClamp(width), height: evenClamp(height) };
}

/**
 * The Size row's four classes (round 6, I3 -- QA#2 / UX#2). `original` is the first photo's own
 * pixels, `p720` / `p1080` are the broadcast classes, `custom` is anything else.
 */
export type SizeClass = 'original' | 'p720' | 'p1080' | 'custom';

/**
 * What "720p" / "1080p" MEAN here: the frame's SHORTER side. For a landscape or square shape that
 * is the height, which is what the names have always said (1280 x 720, 1920 x 1080, 1080 x 1080);
 * for a tall shape it is the width, because a 1080p Reel is 1080 x 1920 -- the 16:9 frame turned
 * on its side -- and nobody calls a 608 x 1080 video "1080p". Round 6 (QA#2, UX#2): the old rule
 * fixed the HEIGHT and read the aspect off the first photo, so "9:16 then 1080p" produced
 * 866 x 1080 (4:5) and the Reel use case could not be reached except by typing.
 */
export const SIZE_CLASS_SHORT_SIDE: Readonly<Record<'p720' | 'p1080', number>> = Object.freeze({
    p720: 720,
    p1080: 1080,
});

/** How far a pixel count may stray from the first photo's before it stops being "Original". */
const ORIGINAL_AREA_TOLERANCE = 0.02;

/** The frame of `ratio` whose shorter side is `shortSide` pixels, evened and clamped. */
export function frameForShortSide(ratio: number, shortSide: number): Size {
    const aspect = ratio > 0 && Number.isFinite(ratio) ? ratio : 16 / 9;
    return aspect >= 1
        ? { width: evenClamp(shortSide * aspect), height: evenClamp(shortSide) }
        : { width: evenClamp(shortSide), height: evenClamp(shortSide / aspect) };
}

/**
 * Which Size class a frame is, read back from its pixels so the row can show what is in force.
 *
 * Exactly the base is `original`; a frame carrying the base's pixel COUNT at another shape (what
 * `composeFrame` makes of Original + 9:16) is `original` too, so the segment the user pressed stays
 * pressed after a shape change; then the two short-side classes; else `custom`. A 1080-tall first
 * photo IS "Original", not "1080p" -- the exact match is tested first.
 */
export function sizeClassOf(size: Size, base: Size | null | undefined): SizeClass {
    if (base && size.width === base.width && size.height === base.height) return 'original';
    if (base && base.width > 0 && base.height > 0) {
        const area = size.width * size.height;
        const baseArea = base.width * base.height;
        if (Math.abs(area - baseArea) / baseArea < ORIGINAL_AREA_TOLERANCE) return 'original';
    }
    const short = Math.min(size.width, size.height);
    if (short === SIZE_CLASS_SHORT_SIDE.p720) return 'p720';
    if (short === SIZE_CLASS_SHORT_SIDE.p1080) return 'p1080';
    return 'custom';
}

/**
 * Shape and Size COMPOSE (round 6, QA#2 / UX#2): the frame for `ratio` (null = the first photo's
 * own shape) at `sizeClass`, where `base` is the first photo's oriented size and `current` the
 * frame as it stands.
 *
 *   9:16 + 1080p = 1080 x 1920 · 16:9 + 720p = 1280 x 720 · 1:1 + 1080p = 1080 x 1080
 *   1080p 9:16 -> 4:5 = 1080 x 1350 (the class survives the shape change)
 *   Original + 9:16 = the first photo's pixel count at 9:16 (`frameForRatio`, as before)
 *   Custom + a shape = the CURRENT pixel count at that shape (a typed size keeps its cost)
 *
 * The control calls this from both rows with the OTHER row's current value, which is the whole
 * fix: before, the Size row derived its aspect from the first photo and the Shape row derived
 * its size from the first photo, so each press undid the other.
 */
export function composeFrame(ratio: number | null, sizeClass: SizeClass, base: Size, current: Size): Size {
    const baseAspect = base.width > 0 && base.height > 0 ? base.width / base.height : 16 / 9;
    const aspect = ratio ?? baseAspect;
    switch (sizeClass) {
        case 'p720':
        case 'p1080':
            return frameForShortSide(aspect, SIZE_CLASS_SHORT_SIDE[sizeClass]);
        case 'original':
            return ratio === null
                ? { width: evenClamp(base.width), height: evenClamp(base.height) }
                : frameForRatio(base, ratio);
        default:
            return frameForRatio(current, aspect);
    }
}

/**
 * The frame's shorter side would be less than half the largest source's (round 6, SDE#9).
 *
 * The default frame follows the FIRST clip, and after a reorder that can be a 208 x 244 thumbnail
 * in a project of 6000 x 4500 photos: the export was 208 x 244 and nothing but the sheet's Frame
 * line said so. This is the test behind the one-line warning the frame popover and the export
 * sheet show ("Your video will be 208 x 244 -- pick a Size to export larger"). Only while the
 * frame is the DEFAULT (`touched` false): a size the user chose is a size the user chose. Half a
 * side (a quarter of the pixels) rather than "any source is bigger", so a 1440 x 1080 default
 * beside a 1920 x 1080 photo -- a scale most people would call the same -- says nothing.
 */
export function frameBelowSources(frame: Pick<FrameState, 'size' | 'touched'>, sources: readonly Size[]): boolean {
    if (frame.touched) return false;
    let largest = 0;
    for (const s of sources) {
        if (s && s.width > 0 && s.height > 0) largest = Math.max(largest, s.width * s.height);
    }
    if (largest === 0) return false;
    return frame.size.width * frame.size.height * 4 < largest;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * The largest crop of `frameAspect` that fits inside a source of `orientedW x orientedH`,
 * centred where the previous crop was centred (or in the middle when there was none).
 *
 * The returned crop satisfies the server's rule by construction — `(w·W)/(h·H)` equals
 * `frameAspect` up to the six-decimal rounding, well inside the 1% tolerance — which
 * `frame.spec.ts` asserts rather than assumes, because a crop that fails it is a refusal the
 * user cannot act on ("Re-frame it and try again" on a crop they never made).
 *
 * The centre is kept only as far as the source allows: a crop wider than the room left beside
 * the old centre stops at the edge instead of hanging over it, which is the closest it can be
 * to what the user framed.
 */
export function centredCropFor(
    orientedW: number,
    orientedH: number,
    frameAspect: number,
    previous?: Crop | null,
): Crop {
    const sourceAspect = orientedW / orientedH;
    let w = 1;
    let h = 1;
    if (sourceAspect > frameAspect) w = frameAspect / sourceAspect;
    else h = sourceAspect / frameAspect;
    w = clamp(w, MIN_CROP_FRACTION, 1);
    h = clamp(h, MIN_CROP_FRACTION, 1);
    const cx = previous ? previous.x + previous.w / 2 : 0.5;
    const cy = previous ? previous.y + previous.h / 2 : 0.5;
    return {
        x: round6(clamp(cx - w / 2, 0, 1 - w)),
        y: round6(clamp(cy - h / 2, 0, 1 - h)),
        w: round6(w),
        h: round6(h),
    };
}

/** 6.3's "shape change re-centres crops": the same centre, the new shape. */
export function recentreCrop(
    previous: Crop | null,
    orientedW: number,
    orientedH: number,
    frame: Size,
): Crop {
    return centredCropFor(orientedW, orientedH, frame.width / frame.height, previous);
}

/**
 * `(w·h/1e6)·fps·totalSeconds` — the cost `studio.maxOutputMegapixelFrames` bounds.
 *
 * Stated here for the UI's budget meter and again inline in `check.ts` for the refusal.
 * Two lines, deliberately duplicated rather than imported, because `check.ts` is written
 * before this file and an import would invert the task order; `frame.spec.ts` asserts the
 * two agree -- both on a worked case and through `checkDocument` itself at the exact
 * millisecond the refusal starts -- which is the guard that makes the duplication safe.
 */
export function costMegapixelFrames(frame: Size, fps: number, totalMs: number): number {
    return ((frame.width * frame.height) / 1_000_000) * fps * (totalMs / 1000);
}

/**
 * The frame's background colour: what fills the bars AND, since the transparency fix, what a
 * transparent source is flattened onto before anything else happens to it (owner ruling: "the image
 * on your chosen colour" -- ironman flattens at the head of the clip's filter graph, and the preview
 * flattens the still it hands the compositor, `media/flatten.ts`). The schema's `#rrggbb`
 * (`composition-v1.schema.json` `backgroundColor.pattern`); written upper-case here.
 */
export const BACKGROUND_BLACK = '#000000';
export const BACKGROUND_WHITE = '#FFFFFF';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** `#rrggbb` upper-cased, or null for anything the schema would refuse (`#fff`, `red`, a number). */
export function normaliseBackgroundColor(value: unknown): string | null {
    return typeof value === 'string' && HEX_COLOR.test(value) ? value.toUpperCase() : null;
}

/**
 * The colour a NEW project takes from its first clip: white when that clip is an image (a still or
 * an animated GIF/WebP/APNG -- the file, not the clip kind it became), black for a video.
 *
 * The regular converter's rule, `DEFAULT_BG_WHITE_FOR_IMAGE_SOURCES` in xconfig (a `fromMedia ==
 * IMAGE` page defaults `videoBackgroundColor` to `#FFFFFF`: "white matches the transparent->JPG matte
 * and the ezgif convention; dark logos stay visible. Video sources keep the black letterbox"), so a
 * logo PNG looks the same in the Studio as on `/convert-png-to-mp4`.
 */
export function defaultBackgroundFor(firstKind: 'image' | 'video' | 'audio' | null | undefined): string {
    return firstKind === 'image' ? BACKGROUND_WHITE : BACKGROUND_BLACK;
}

export interface FrameState {
    size: Size;
    fps: Fps;
    fit: Fit;
    backgroundColor: string;
    /** True once the user has chosen a frame; a later clip never moves it after that. */
    touched: boolean;
    /**
     * True once the user has chosen a background colour (`withBackgroundColor`). A SECOND latch,
     * not `touched`: `touched` is about the SIZE (see `withFps`), and picking white must not pin the
     * 1280 x 720 fallback against the first photo. What this one stops is the first-clip default
     * (`withFirstClipBackground`) overwriting a colour the user picked on an empty project.
     * Persisted beside `frameTouched` (`StudioUiState.backgroundTouched`); optional, and absent reads
     * as "not chosen" -- a record written before it existed has clips, so the default never runs.
     */
    backgroundTouched?: boolean;
}

/**
 * The editing view of the document's frame — the ONLY way a view should obtain a `FrameState`.
 *
 * Derived on every call rather than held: `doc.frame` is the truth (review I6), so an undo, a
 * restore and a two-tab reconcile all move the frame by moving the document, and the state a
 * panel edits follows without a second copy to keep in step. The latch is the one bit the
 * wire has no field for, so it is read from the persisted ui state.
 *
 * DEFENSIVE ABOUT WHAT IT READS. `doc` can come straight out of localStorage, which anything
 * on the origin can write: a non-even, out-of-range or non-numeric size is clamped back into
 * the schema's `16..4096` rather than handed to a canvas, and an fps outside the enum falls
 * back to the default instead of producing a frame grid nothing can render.
 */
export function frameStateFor(
    doc: Pick<StudioDocument, 'frame'>,
    ui?: { frameTouched?: boolean; backgroundTouched?: boolean } | null,
): FrameState {
    const { width, height, fps, fit, backgroundColor } = doc.frame;
    const size = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
        ? { width: evenClamp(width), height: evenClamp(height) }
        : { ...FALLBACK_SIZE };
    return {
        size,
        fps: (FPS_CHOICES as readonly number[]).includes(fps) ? fps : DEFAULT_FPS,
        fit: fit ?? DEFAULT_FIT,
        // The stored colour as it is (no migration: a saved project keeps its colour, black
        // included); only a value the schema would refuse falls back.
        backgroundColor: typeof backgroundColor === 'string' && HEX_COLOR.test(backgroundColor) ? backgroundColor : BACKGROUND_BLACK,
        touched: ui?.frameTouched === true,
        backgroundTouched: ui?.backgroundTouched === true,
    };
}

/**
 * The seed for a project that has no document yet.
 *
 * A VIEW DOES NOT CALL THIS (review I6). Once there is a document, `frameStateFor(doc, ui)` is
 * the state, and an `initialFrameState()` evaluated inline on a render would reset the latch
 * on every frame the user drew.
 */
export function initialFrameState(
    first?: { natural: Size; rotate?: 0 | 90 | 180 | 270; kind?: 'image' | 'video' } | null,
): FrameState {
    return {
        size: first ? frameFromFirstClip(first.natural, first.rotate ?? 0) : { ...FALLBACK_SIZE },
        fps: DEFAULT_FPS,
        fit: DEFAULT_FIT,
        // An empty project is black (nothing to flatten, and the empty stage stays the dark box it
        // always was); the first clip placed decides (`withFirstClipBackground`).
        backgroundColor: first?.kind ? defaultBackgroundFor(first.kind) : BACKGROUND_BLACK,
        touched: false,
        backgroundTouched: false,
    };
}

/**
 * The first clip's background default, applied ONCE -- when a project with no clips gets its first
 * one -- and only while the user has not chosen a colour. The caller decides "first" (the
 * placement into an empty document); this decides the colour. A later reorder that puts a video
 * first does NOT flip it: a colour that changes under the user because of a drag is worse than a
 * default that stays.
 */
export function withFirstClipBackground(state: FrameState, firstKind: 'image' | 'video' | 'audio'): FrameState {
    if (state.backgroundTouched) return state;
    const color = defaultBackgroundFor(firstKind);
    return color === state.backgroundColor ? state : { ...state, backgroundColor: color };
}

/**
 * A user-chosen background colour. Latches `backgroundTouched` -- never `touched`, which is about
 * the SIZE (the same reason `withFps` does not). A value the schema would refuse is ignored.
 */
export function withBackgroundColor(state: FrameState, color: string): FrameState {
    const next = normaliseBackgroundColor(color);
    if (!next) return state;
    if (state.backgroundTouched && next === state.backgroundColor.toUpperCase()) return state;
    return { ...state, backgroundColor: next, backgroundTouched: true };
}

/** The first clip's size, applied only while the user has not chosen one. */
export function withFirstClipDefault(
    state: FrameState,
    natural: Size,
    rotate: 0 | 90 | 180 | 270 = 0,
): FrameState {
    if (state.touched) return state;
    const size = frameFromFirstClip(natural, rotate);
    if (size.width === state.size.width && size.height === state.size.height) return state;
    return { ...state, size };
}

/** A user-chosen frame. Latches `touched`, so nothing moves it back afterwards. */
export function withFrame(state: FrameState, size: Size): FrameState {
    const next = { width: evenClamp(size.width), height: evenClamp(size.height) };
    if (state.touched && next.width === state.size.width && next.height === state.size.height) return state;
    return { ...state, size: next, touched: true };
}

/**
 * A user-chosen frame RATE. Does NOT latch `touched`, because `touched` is about the SIZE.
 *
 * P5a.4 review (M2): latching here meant that picking 60 fps before adding the first photo
 * pinned the 1280x720 fallback, and the photo that arrived a second later was then refused
 * the frame it should have set. The two are separate concerns -- only `withFrame` and
 * `withFit`-adjacent explicit size edits say anything about what SIZE the user wants.
 */
export function withFps(state: FrameState, fps: Fps): FrameState {
    return fps === state.fps ? state : { ...state, fps };
}

export function withFit(state: FrameState, fit: Fit): FrameState {
    return fit === state.fit ? state : { ...state, fit };
}

/** The `Frame` the wire carries, from the editor's state. */
export function toFrame(state: FrameState): Frame {
    return {
        width: state.size.width,
        height: state.size.height,
        fps: state.fps,
        fit: state.fit,
        backgroundColor: state.backgroundColor,
    };
}
